import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    GitBranch, Plus, Play, CheckCircle, Upload, Trash2, X, RotateCcw, ChevronLeft,
} from 'lucide-react';
import { getClients, getClientSchemes } from '../services/customer.service';
import { diagnosticService, triageService } from '../services/diagnostic.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDialog } from '../contexts/DialogContext';

const NODE_TYPES = ['QUESTION', 'INFO', 'COMPUTE', 'OUTCOME'];
const OUTCOME_TYPES = ['ADVICE', 'TOW', 'ROS', 'ESCALATE', 'EMERGENCY'];
const NODE_W = 176;
const NODE_H = 72;

function newKey() {
    return `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

const Diagnostics = () => {
    const { confirm } = useDialog();
    const [clients, setClients] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedSchemeId, setSelectedSchemeId] = useState('');
    const [flows, setFlows] = useState([]);
    const [catalog, setCatalog] = useState({ entry_categories: [], variables: [], ops: [], outcome_types: [], service_types: [], service_subtypes: [], causes_of_call: [] });
    const [flow, setFlow] = useState(null);
    const [versionId, setVersionId] = useState(null);
    const [versionMeta, setVersionMeta] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [outline, setOutline] = useState(false);
    const [tab, setTab] = useState('Content');
    const [loading, setLoading] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [message, setMessage] = useState('');
    const [validation, setValidation] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [newFlow, setNewFlow] = useState({ code: '', name: '', entry_category: 'WONT_START', template_id: '' });
    const [zoom, setZoom] = useState(1);

    // Simulator drawer
    const [simOpen, setSimOpen] = useState(false);
    const [sim, setSim] = useState(null);
    const [simBusy, setSimBusy] = useState(false);
    const [simErr, setSimErr] = useState('');

    // Canvas interaction
    const containerRef = useRef(null);
    const dragRef = useRef(null);
    const connectRef = useRef(null);
    const [connectPos, setConnectPos] = useState(null);

    const commit = (updater) => {
        setNodes(updater);
        setDirty(true);
    };

    useEffect(() => {
        diagnosticService.catalog().then(setCatalog).catch(() => {});
        diagnosticService.listTemplates().then((r) => setTemplates(Array.isArray(r) ? r : [])).catch(() => setTemplates([]));
        getClients({ is_active: true }).then((r) => {
            const list = r || [];
            setClients(list);
            if (list.length === 1) setSelectedClientId(list[0].id);
        }).catch(() => setClients([]));
    }, []);

    useEffect(() => {
        if (!selectedClientId) {
            setSchemes([]);
            setSelectedSchemeId('');
            return;
        }
        getClientSchemes(selectedClientId, { is_active: true }).then((r) => {
            const list = r || [];
            setSchemes(list);
            if (list.length === 1) setSelectedSchemeId(list[0].id);
        }).catch(() => setSchemes([]));
    }, [selectedClientId]);

    const loadFlows = async (schemeId) => {
        if (!schemeId) {
            setFlows([]);
            return;
        }
        const rows = await diagnosticService.listFlows(schemeId);
        setFlows(Array.isArray(rows) ? rows : []);
        diagnosticService.analytics(schemeId).then(setAnalytics).catch(() => setAnalytics(null));
    };

    useEffect(() => {
        loadFlows(selectedSchemeId).catch(() => setFlows([]));
        if (selectedSchemeId) diagnosticService.catalog(selectedSchemeId).then(setCatalog).catch(() => {});
    }, [selectedSchemeId]);

    useEffect(() => {
        const h = (e) => {
            if (dirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [dirty]);

    // Debounced autosave: quiet PUT of the draft graph 2.5s after edits settle.
    useEffect(() => {
        if (!dirty || !versionId || loading) return undefined;
        const id = setTimeout(async () => {
            try {
                await diagnosticService.putGraph(versionId, nodes);
                setDirty(false);
                setMessage('Autosaved');
            } catch {
                /* leave dirty; manual Save / next tick retries */
            }
        }, 2500);
        return () => clearTimeout(id);
    }, [dirty, nodes, versionId, loading]);

    const openFlow = async (row) => {
        if (dirty && !(await confirm({
            title: 'Unsaved changes',
            message: 'Discard unsaved changes to the current flow?',
            confirmLabel: 'Discard',
            danger: false,
        }))) return;
        setLoading(true);
        setMessage('');
        try {
            const detail = await diagnosticService.getFlow(row.id);
            setFlow(detail);
            const vid = detail.draft_version_id || detail.published_version_id;
            setVersionId(vid);
            if (vid) {
                const v = await diagnosticService.getVersion(vid);
                setVersionMeta(v.version);
                const graphNodes = (v.graph?.nodes || []).map((n) => ({
                    ...n,
                    client_key: String(n.id),
                }));
                setNodes(graphNodes);
                setDirty(false);
                const start = graphNodes.find((n) => n.is_start) || graphNodes[0];
                setSelectedKey(start?.client_key || null);
            }
        } catch (e) {
            setMessage(e.message || 'Failed to load flow');
        } finally {
            setLoading(false);
        }
    };

    const selected = nodes.find((n) => n.client_key === selectedKey);

    const updateNode = (patch) => {
        commit((prev) => prev.map((n) => (n.client_key === selectedKey ? { ...n, ...patch } : n)));
    };

    const updateOutcome = (patch) => {
        updateNode({ outcome: { outcome_type: 'ADVICE', ...(selected?.outcome || {}), ...patch } });
    };

    const updateTransition = (i, patch) => {
        const list = [...(selected.transitions || [])];
        list[i] = { ...list[i], ...patch };
        updateNode({ transitions: list });
    };

    const removeTransition = (i) => {
        updateNode({ transitions: (selected.transitions || []).filter((_, x) => x !== i) });
    };

    const addNode = (type) => {
        const key = newKey();
        const node = {
            client_key: key,
            code: `${type.toLowerCase()}_${nodes.length + 1}`,
            type,
            prompt_customer: '',
            prompt_agent: '',
            internal_note: '',
            channel_visibility: 'BOTH',
            is_safety_gate: type === 'QUESTION' && nodes.length < 2,
            is_start: nodes.length === 0,
            layout_x: 40 + (nodes.length % 4) * 220,
            layout_y: 40 + Math.floor(nodes.length / 4) * 140,
            options: type === 'QUESTION' ? [
                { label: 'Yes', value: 'yes', sort_order: 0 },
                { label: 'No', value: 'no', sort_order: 1 },
            ] : type === 'INFO' ? [{ label: 'Continue', value: '_continue', sort_order: 0 }] : [],
            transitions: [],
            outcome: type === 'OUTCOME' ? { outcome_type: 'ADVICE', advice_text: '', chargeable: false } : null,
        };
        commit((prev) => [...prev, node]);
        setSelectedKey(key);
    };

    const deleteNode = (key) => {
        const victim = nodes.find((n) => n.client_key === key);
        if (!victim) return;
        const dead = new Set([String(key)]);
        if (victim.id != null) dead.add(String(victim.id));
        commit((prev) => prev
            .filter((n) => n.client_key !== key)
            .map((n) => ({
                ...n,
                options: (n.options || []).filter((o) => !dead.has(String(o.target_client_key)) && !dead.has(String(o.target_node_id))),
                transitions: (n.transitions || []).filter((t) => !dead.has(String(t.target_client_key)) && !dead.has(String(t.target_node_id))),
            })));
        setSelectedKey((cur) => (cur === key ? null : cur));
    };

    const connectNodes = (fromKey, toKey) => {
        commit((prev) => prev.map((n) => {
            if (n.client_key !== fromKey) return n;
            if (n.type === 'QUESTION') {
                const idx = (n.options || []).length + 1;
                return {
                    ...n,
                    options: [...(n.options || []), {
                        label: `Answer ${idx}`, value: `ans_${idx}`, sort_order: idx - 1,
                        target_client_key: toKey, target_node_id: toKey,
                    }],
                };
            }
            if (n.type === 'INFO') {
                const opts = n.options && n.options.length
                    ? [...n.options]
                    : [{ label: 'Continue', value: '_continue', sort_order: 0 }];
                opts[0] = { ...opts[0], target_client_key: toKey, target_node_id: toKey };
                return { ...n, options: opts };
            }
            if (n.type === 'COMPUTE') {
                const idx = (n.transitions || []).length;
                return {
                    ...n,
                    transitions: [...(n.transitions || []), {
                        condition_json: { var: '', op: 'eq', value: '' }, priority: idx,
                        target_client_key: toKey, target_node_id: toKey,
                    }],
                };
            }
            return n;
        }));
    };

    // ---- canvas mouse handlers -------------------------------------------------
    const canvasPoint = (e) => {
        const el = containerRef.current;
        const rect = el.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left + el.scrollLeft) / zoom,
            y: (e.clientY - rect.top + el.scrollTop) / zoom,
        };
    };

    const onNodeMouseDown = (e, n) => {
        if (e.button !== 0) return;
        setSelectedKey(n.client_key);
        const p = canvasPoint(e);
        dragRef.current = { key: n.client_key, offX: p.x - (Number(n.layout_x) || 0), offY: p.y - (Number(n.layout_y) || 0) };
    };

    const onConnectStart = (e, n) => {
        e.stopPropagation();
        e.preventDefault();
        connectRef.current = { key: n.client_key };
        setConnectPos(canvasPoint(e));
    };

    const onNodeMouseUp = (n) => {
        if (connectRef.current && connectRef.current.key !== n.client_key) {
            connectNodes(connectRef.current.key, n.client_key);
        }
        connectRef.current = null;
        setConnectPos(null);
    };

    const onCanvasMouseMove = (e) => {
        if (!dragRef.current && !connectRef.current) return;
        const p = canvasPoint(e);
        if (dragRef.current) {
            const { key, offX, offY } = dragRef.current;
            const nx = Math.max(0, p.x - offX);
            const ny = Math.max(0, p.y - offY);
            setNodes((prev) => prev.map((n) => (n.client_key === key ? { ...n, layout_x: nx, layout_y: ny } : n)));
            setDirty(true);
        }
        if (connectRef.current) setConnectPos(p);
    };

    const onCanvasMouseUp = () => {
        dragRef.current = null;
        connectRef.current = null;
        setConnectPos(null);
    };

    const targetKeysOf = (n, idToKey, byKey) => {
        const ks = [];
        [...(n.options || []), ...(n.transitions || [])].forEach((x) => {
            const k = x.target_client_key || idToKey[String(x.target_node_id)];
            if (k && byKey[k]) ks.push(k);
        });
        return ks;
    };

    const autoLayout = () => {
        if (!nodes.length) return;
        const byKey = Object.fromEntries(nodes.map((n) => [n.client_key, n]));
        const idToKey = Object.fromEntries(nodes.map((n) => [String(n.id), n.client_key]));
        const start = nodes.find((n) => n.is_start) || nodes[0];
        const depth = { [start.client_key]: 0 };
        const queue = [start.client_key];
        while (queue.length) {
            const k = queue.shift();
            targetKeysOf(byKey[k], idToKey, byKey).forEach((tk) => {
                if (depth[tk] == null) {
                    depth[tk] = depth[k] + 1;
                    queue.push(tk);
                }
            });
        }
        let maxD = Math.max(0, ...Object.values(depth));
        nodes.forEach((n) => {
            if (depth[n.client_key] == null) {
                maxD += 1;
                depth[n.client_key] = maxD;
            }
        });
        const tiers = {};
        nodes.forEach((n) => {
            const d = depth[n.client_key];
            (tiers[d] = tiers[d] || []).push(n.client_key);
        });
        const COL_W = 240;
        const ROW_H = 130;
        commit((prev) => prev.map((n) => {
            const d = depth[n.client_key];
            const row = tiers[d].indexOf(n.client_key);
            return { ...n, layout_x: 40 + d * COL_W, layout_y: 40 + row * ROW_H };
        }));
    };

    const fitToView = () => {
        const el = containerRef.current;
        if (!el || !nodes.length) return;
        const maxX = Math.max(...nodes.map((n) => (Number(n.layout_x) || 0) + NODE_W)) + 40;
        const maxY = Math.max(...nodes.map((n) => (Number(n.layout_y) || 0) + NODE_H)) + 40;
        const z = Math.min(1, el.clientWidth / maxX, el.clientHeight / maxY);
        setZoom(Math.max(0.3, Number(z.toFixed(2))));
        el.scrollTo({ left: 0, top: 0 });
    };

    const focusNodeByMsg = (msg) => {
        const m = /'([^']+)'/.exec(msg || '');
        const n = m && nodes.find((x) => x.code === m[1]);
        if (n) {
            setSelectedKey(n.client_key);
            if (outline) return;
            const el = containerRef.current;
            if (el) {
                el.scrollTo({
                    left: Math.max(0, (Number(n.layout_x) || 0) * zoom - 120),
                    top: Math.max(0, (Number(n.layout_y) || 0) * zoom - 80),
                    behavior: 'smooth',
                });
            }
        }
    };

    useEffect(() => {
        const onKey = (e) => {
            if (!selectedKey) return;
            const tag = (e.target.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                deleteNode(selectedKey);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, nodes]);

    const saveGraph = async () => {
        if (!versionId) return;
        setLoading(true);
        try {
            await diagnosticService.putGraph(versionId, nodes);
            setDirty(false);
            setMessage('Draft saved');
        } catch (e) {
            setMessage(e.message || 'Save failed');
        } finally {
            setLoading(false);
        }
    };

    const runValidate = async () => {
        await saveGraph();
        const res = await diagnosticService.validate(versionId);
        setValidation(res);
    };

    const runPublish = async () => {
        await saveGraph();
        try {
            const updated = await diagnosticService.publish(versionId);
            setMessage('Published');
            setFlow(updated);
            if (updated.draft_version_id) {
                setVersionId(updated.draft_version_id);
                const v = await diagnosticService.getVersion(updated.draft_version_id);
                setVersionMeta(v.version);
                setNodes((v.graph?.nodes || []).map((n) => ({ ...n, client_key: String(n.id) })));
                setDirty(false);
            }
            await loadFlows(selectedSchemeId);
        } catch (e) {
            const detail = e.errorData?.detail;
            if (detail?.errors) setValidation({ ok: false, errors: detail.errors, warnings: detail.warnings || [] });
            setMessage(typeof detail === 'string' ? detail : (detail?.message || e.message || 'Publish failed'));
        }
    };

    const createFlow = async () => {
        if (!selectedSchemeId) return;
        if (!newFlow.code.trim() || !newFlow.name.trim()) {
            setMessage('Code and name are required');
            return;
        }
        try {
            const body = {
                client_scheme_id: Number(selectedSchemeId),
                code: newFlow.code,
                name: newFlow.name,
                entry_category: newFlow.entry_category,
            };
            const created = newFlow.template_id
                ? await diagnosticService.instantiate(Number(newFlow.template_id), body)
                : await diagnosticService.createFlow(body);
            setNewFlow({ code: '', name: '', entry_category: 'WONT_START', template_id: '' });
            await loadFlows(selectedSchemeId);
            await openFlow(created);
        } catch (e) {
            setMessage(e.message || 'Create failed');
        }
    };

    const deleteFlowRow = async (row) => {
        const ok = await confirm({
            title: 'Delete flow',
            message: `Delete flow "${row.name}"? This cannot be undone.`,
            confirmLabel: 'Delete',
        });
        if (!ok) return;
        try {
            await diagnosticService.deleteFlow(row.id);
            if (flow?.id === row.id) {
                setFlow(null);
                setVersionId(null);
                setVersionMeta(null);
                setNodes([]);
                setSelectedKey(null);
                setValidation(null);
                setDirty(false);
            }
            setMessage(`Deleted "${row.name}"`);
            await loadFlows(selectedSchemeId);
        } catch (e) {
            setMessage(e.message || 'Delete failed');
        }
    };

    // ---- simulator ----------------------------------------------------------
    const simStart = async () => {
        if (!flow || !versionId) return;
        setSimBusy(true);
        setSimErr('');
        try {
            await saveGraph();
            const s = await triageService.start({
                schemeId: Number(selectedSchemeId),
                entryCategory: flow.entry_category,
                channel: 'AGENT',
                versionId,
                context: {},
            });
            setSim(s);
        } catch (e) {
            setSimErr(e.message || 'Could not start run');
            setSim(null);
        } finally {
            setSimBusy(false);
        }
    };

    const simAnswer = async (value) => {
        if (!sim?.sessionId || !sim?.node) return;
        setSimBusy(true);
        setSimErr('');
        try {
            setSim(await triageService.answer(sim.sessionId, sim.node.id, value));
        } catch (e) {
            setSimErr(e.message || 'Answer rejected');
        } finally {
            setSimBusy(false);
        }
    };

    const simBack = async () => {
        if (!sim?.sessionId) return;
        setSimBusy(true);
        setSimErr('');
        try {
            setSim(await triageService.back(sim.sessionId));
        } catch (e) {
            setSimErr(e.message || 'Back failed');
        } finally {
            setSimBusy(false);
        }
    };

    const edges = useMemo(() => {
        const byKey = Object.fromEntries(nodes.map((n) => [n.client_key, n]));
        const byId = Object.fromEntries(nodes.map((n) => [String(n.id), n]));
        const out = [];
        nodes.forEach((n) => {
            (n.options || []).forEach((o) => {
                const t = byKey[String(o.target_client_key)] || byId[String(o.target_node_id)];
                if (t) out.push({ from: n, to: t, label: o.label });
            });
            (n.transitions || []).forEach((tr) => {
                const t = byKey[String(tr.target_client_key)] || byId[String(tr.target_node_id)];
                if (t) out.push({ from: n, to: t, label: 'if' });
            });
        });
        return out;
    }, [nodes]);

    const grouped = useMemo(() => {
        const g = {};
        flows.forEach((f) => {
            const k = f.entry_category || 'OTHER';
            g[k] = g[k] || [];
            g[k].push(f);
        });
        return g;
    }, [flows]);

    const canvasSize = useMemo(() => {
        let w = 1200;
        let h = 800;
        nodes.forEach((n) => {
            w = Math.max(w, (Number(n.layout_x) || 0) + NODE_W + 160);
            h = Math.max(h, (Number(n.layout_y) || 0) + NODE_H + 200);
        });
        return { w, h };
    }, [nodes]);

    const validationByCode = useMemo(() => {
        const map = {};
        if (!validation) return map;
        const tag = (arr, kind) => (arr || []).forEach((msg) => {
            nodes.forEach((n) => {
                if (n.code && msg.includes(`'${n.code}'`)) {
                    map[n.code] = map[n.code] || { errors: [], warnings: [] };
                    map[n.code][kind].push(msg);
                }
            });
        });
        tag(validation.errors, 'errors');
        tag(validation.warnings, 'warnings');
        return map;
    }, [validation, nodes]);

    const connectSrc = connectPos && connectRef.current
        ? nodes.find((n) => n.client_key === connectRef.current.key)
        : null;

    return (
        <div className="p-4 h-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <GitBranch className="w-5 h-5" />
                    <h1 className="text-xl font-semibold">Diagnostic flows</h1>
                    {versionMeta && (
                        <span className="text-xs px-2 py-1 rounded bg-gray-800 text-white">
                            {versionMeta.status === 'draft' ? `Draft v${versionMeta.version_no}` : `Published v${versionMeta.version_no}`}
                        </span>
                    )}
                    {dirty && <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">Unsaved</span>}
                </div>
                <div className="flex gap-2">
                    <button type="button" className="px-3 py-1.5 border rounded" onClick={() => setOutline(!outline)}>
                        {outline ? 'Canvas' : 'Outline'}
                    </button>
                    <button type="button" className="px-3 py-1.5 border rounded" onClick={() => setSimOpen(true)} disabled={!versionId}>
                        <Play className="w-4 h-4 inline mr-1" />Simulate
                    </button>
                    <button type="button" className="px-3 py-1.5 border rounded" onClick={runValidate} disabled={!versionId}>
                        <CheckCircle className="w-4 h-4 inline mr-1" />Validate
                    </button>
                    <button type="button" className="px-3 py-1.5 border rounded" onClick={saveGraph} disabled={!versionId}>
                        Save
                    </button>
                    <button type="button" className="px-3 py-1.5 bg-gray-900 text-white rounded" onClick={runPublish} disabled={!versionId}>
                        <Upload className="w-4 h-4 inline mr-1" />Publish
                    </button>
                </div>
            </div>

            <div className="flex gap-3 mb-3">
                <select className="border rounded px-2 py-1" value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)}>
                    <option value="">Client</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                </select>
                <select className="border rounded px-2 py-1" value={selectedSchemeId} onChange={(e) => setSelectedSchemeId(e.target.value)}>
                    <option value="">Scheme</option>
                    {schemes.map((s) => <option key={s.id} value={s.id}>{s.scheme_name}</option>)}
                </select>
                {message && <span className="text-sm text-gray-600 self-center">{message}</span>}
            </div>

            <div className="grid grid-cols-12 gap-3" style={{ minHeight: 560 }}>
                <div className="col-span-12 lg:col-span-2 border rounded p-2 overflow-auto bg-white">
                    <p className="text-xs font-semibold mb-2">Flows</p>
                    {Object.entries(grouped).map(([cat, list]) => (
                        <div key={cat} className="mb-2">
                            <p className="text-[10px] uppercase text-gray-500">{cat}</p>
                            {list.map((f) => (
                                <div
                                    key={f.id}
                                    className={`group flex items-center rounded ${flow?.id === f.id ? 'bg-gray-800 text-white' : 'hover:bg-gray-100'}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => openFlow(f)}
                                        className="flex-1 text-left text-sm px-2 py-1 truncate"
                                    >
                                        {f.name}
                                    </button>
                                    <button
                                        type="button"
                                        title="Delete flow"
                                        onClick={() => deleteFlowRow(f)}
                                        className={`px-1.5 opacity-0 group-hover:opacity-100 ${flow?.id === f.id ? 'text-white' : 'text-red-500'}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ))}
                    <div className="mt-3 border-t pt-2">
                        <p className="text-xs font-semibold mb-1">New flow</p>
                        <select className="w-full border rounded px-2 py-1 text-xs mb-1" value={newFlow.template_id} onChange={(e) => setNewFlow({ ...newFlow, template_id: e.target.value })}>
                            <option value="">Blank flow</option>
                            {templates.map((t) => <option key={t.id} value={t.id}>From: {t.name}</option>)}
                        </select>
                        <input className="w-full border rounded px-2 py-1 text-xs mb-1" placeholder="Code" value={newFlow.code} onChange={(e) => setNewFlow({ ...newFlow, code: e.target.value })} />
                        <input className="w-full border rounded px-2 py-1 text-xs mb-1" placeholder="Name" value={newFlow.name} onChange={(e) => setNewFlow({ ...newFlow, name: e.target.value })} />
                        <select className="w-full border rounded px-2 py-1 text-xs mb-1" value={newFlow.entry_category} onChange={(e) => setNewFlow({ ...newFlow, entry_category: e.target.value })}>
                            {(catalog.entry_categories || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        <button type="button" className="w-full text-xs border rounded py-1" onClick={createFlow}><Plus className="w-3 h-3 inline" /> {newFlow.template_id ? 'Create from template' : 'Create'}</button>
                    </div>
                    <p className="text-xs font-semibold mt-3 mb-1">Palette</p>
                    {NODE_TYPES.map((t) => (
                        <button key={t} type="button" className="block w-full text-left text-xs px-2 py-1 hover:bg-gray-100" onClick={() => addNode(t)}>{t}</button>
                    ))}
                    {analytics && (
                        <div className="mt-3 text-[11px] text-gray-600">
                            <p>Sessions {analytics.sessions} · done {analytics.completed}</p>
                            {analytics.avg_answers_to_outcome != null && <p>Avg Qs {analytics.avg_answers_to_outcome}</p>}
                            {analytics.accuracy != null && <p>Accuracy {Math.round(analytics.accuracy * 100)}%</p>}
                        </div>
                    )}
                </div>

                <div className="col-span-12 lg:col-span-7 flex flex-col min-h-[560px]">
                    {!outline && (
                        <div className="flex items-center gap-1 mb-1 text-xs">
                            <button type="button" className="border rounded px-2 py-1" onClick={autoLayout} disabled={!nodes.length}>Auto-layout</button>
                            <button type="button" className="border rounded px-2 py-1" onClick={() => setZoom((z) => Math.max(0.3, Number((z - 0.1).toFixed(2))))}>−</button>
                            <span className="w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                            <button type="button" className="border rounded px-2 py-1" onClick={() => setZoom((z) => Math.min(1.5, Number((z + 0.1).toFixed(2))))}>+</button>
                            <button type="button" className="border rounded px-2 py-1" onClick={fitToView} disabled={!nodes.length}>Fit</button>
                        </div>
                    )}
                    <div
                        ref={containerRef}
                        className="flex-1 border rounded bg-slate-50 relative overflow-auto select-none"
                        onMouseMove={onCanvasMouseMove}
                        onMouseUp={onCanvasMouseUp}
                        onMouseLeave={onCanvasMouseUp}
                    >
                    {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60"><LoadingSpinner /></div>}
                    {outline ? (
                        <div className="p-3 space-y-2">
                            {nodes.map((n) => (
                                <div key={n.client_key} className="flex items-start gap-2 border rounded p-2 bg-white">
                                    <button type="button" className="flex-1 text-left" onClick={() => setSelectedKey(n.client_key)}>
                                        <span className="text-[10px] uppercase text-gray-500">{n.type}</span>
                                        <input className="w-full text-sm border-b" value={n.prompt_customer || ''} onChange={(e) => {
                                            setSelectedKey(n.client_key);
                                            commit((prev) => prev.map((x) => (x.client_key === n.client_key ? { ...x, prompt_customer: e.target.value } : x)));
                                        }} />
                                    </button>
                                    <button type="button" className="text-red-500" onClick={() => deleteNode(n.client_key)}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ width: canvasSize.w * zoom, height: canvasSize.h * zoom }}>
                        <div className="relative" style={{ width: canvasSize.w, height: canvasSize.h, transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
                            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                                <defs>
                                    <marker id="dg-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                                        <path d="M0,0 L10,5 L0,10 z" fill="#334155" />
                                    </marker>
                                </defs>
                                {edges.map((e, i) => {
                                    const fx = (Number(e.from.layout_x) || 0) + NODE_W / 2;
                                    const fy = (Number(e.from.layout_y) || 0) + NODE_H;
                                    const tx = (Number(e.to.layout_x) || 0) + NODE_W / 2;
                                    const ty = (Number(e.to.layout_y) || 0);
                                    return (
                                        <g key={i}>
                                            <line x1={fx} y1={fy} x2={tx} y2={ty} stroke="#334155" strokeWidth="1.5" markerEnd="url(#dg-arrow)" />
                                            {e.label && (
                                                <text
                                                    x={(fx + tx) / 2}
                                                    y={(fy + ty) / 2}
                                                    fontSize="10"
                                                    fill="#475569"
                                                    textAnchor="middle"
                                                    paintOrder="stroke"
                                                    stroke="#f8fafc"
                                                    strokeWidth="3"
                                                >
                                                    {e.label}
                                                </text>
                                            )}
                                        </g>
                                    );
                                })}
                                {connectSrc && (
                                    <line
                                        x1={(Number(connectSrc.layout_x) || 0) + NODE_W / 2}
                                        y1={(Number(connectSrc.layout_y) || 0) + NODE_H}
                                        x2={connectPos.x}
                                        y2={connectPos.y}
                                        stroke="#2563eb"
                                        strokeWidth="1.5"
                                        strokeDasharray="4 3"
                                    />
                                )}
                            </svg>
                            {nodes.map((n) => (
                                <div
                                    key={n.client_key}
                                    onMouseDown={(e) => onNodeMouseDown(e, n)}
                                    onMouseUp={() => onNodeMouseUp(n)}
                                    className={`absolute rounded-md shadow-sm border px-2 py-2 cursor-move ${n.type === 'OUTCOME' ? 'bg-gray-900 text-white' : 'bg-white'} ${selectedKey === n.client_key ? 'ring-2 ring-blue-600' : validationByCode[n.code]?.errors?.length ? 'ring-2 ring-red-500' : validationByCode[n.code]?.warnings?.length ? 'ring-2 ring-amber-400' : ''}`}
                                    style={{ left: Number(n.layout_x) || 0, top: Number(n.layout_y) || 0, width: NODE_W }}
                                >
                                    <div className="flex items-start justify-between gap-1">
                                        <div className="text-[10px] uppercase opacity-70">
                                            {n.type}{n.is_safety_gate ? ' · safety' : ''}{n.is_start ? ' · start' : ''}
                                        </div>
                                        <button
                                            type="button"
                                            className="opacity-50 hover:opacity-100"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={() => deleteNode(n.client_key)}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <div className="text-xs truncate">{n.prompt_customer || n.code}</div>
                                    {n.type === 'QUESTION' && <div className="text-[10px] opacity-70">{(n.options || []).length} answers</div>}
                                    {n.type !== 'OUTCOME' && (
                                        <span
                                            onMouseDown={(e) => onConnectStart(e, n)}
                                            className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-600 border-2 border-white cursor-crosshair"
                                            title="Drag to connect"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        </div>
                    )}
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3 border rounded p-2 bg-white overflow-auto">
                    {!selected && <p className="text-sm text-gray-500">Select a node</p>}
                    {selected && (
                        <>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex gap-1">
                                    {['Content', 'Answers', 'Logic', 'Settings'].map((t) => (
                                        <button key={t} type="button" className={`text-xs px-2 py-1 rounded ${tab === t ? 'bg-gray-900 text-white' : 'border'}`} onClick={() => setTab(t)}>{t}</button>
                                    ))}
                                </div>
                                <button type="button" className="text-red-600" title="Delete node" onClick={() => deleteNode(selected.client_key)}><Trash2 className="w-4 h-4" /></button>
                            </div>
                            {tab === 'Content' && (
                                <div className="space-y-2 text-sm">
                                    <label className="block text-xs">Code<input className="w-full border rounded px-2 py-1" value={selected.code || ''} onChange={(e) => updateNode({ code: e.target.value })} /></label>
                                    <label className="block text-xs">Customer<input className="w-full border rounded px-2 py-1" value={selected.prompt_customer || ''} onChange={(e) => updateNode({ prompt_customer: e.target.value })} /></label>
                                    <label className="block text-xs">Agent script<input className="w-full border rounded px-2 py-1" value={selected.prompt_agent || ''} onChange={(e) => updateNode({ prompt_agent: e.target.value })} /></label>
                                    <label className="block text-xs">Internal note<textarea className="w-full border rounded px-2 py-1" value={selected.internal_note || ''} onChange={(e) => updateNode({ internal_note: e.target.value })} /></label>
                                    {selected.type === 'OUTCOME' && (
                                        <div className="space-y-2 border-t pt-2">
                                            <label className="block text-xs">Outcome type
                                                <select className="w-full border rounded px-2 py-1" value={selected.outcome?.outcome_type || 'ADVICE'} onChange={(e) => updateOutcome({ outcome_type: e.target.value })}>
                                                    {((catalog.outcome_types && catalog.outcome_types.length) ? catalog.outcome_types : OUTCOME_TYPES).map((o) => <option key={o}>{o}</option>)}
                                                </select>
                                            </label>
                                            {['TOW', 'ROS'].includes(selected.outcome?.outcome_type) && !selected.outcome?.scheme_service_type_id && (
                                                <p className="text-[11px] text-amber-700">TOW/ROS needs a service type to publish.</p>
                                            )}
                                            <label className="block text-xs">Service type
                                                <select
                                                    className="w-full border rounded px-2 py-1"
                                                    value={selected.outcome?.scheme_service_type_id || ''}
                                                    onChange={(e) => updateOutcome({
                                                        scheme_service_type_id: e.target.value ? Number(e.target.value) : null,
                                                        scheme_service_subtype_id: null,
                                                    })}
                                                >
                                                    <option value="">—</option>
                                                    {(catalog.service_types || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </label>
                                            <label className="block text-xs">Service subtype
                                                <select
                                                    className="w-full border rounded px-2 py-1"
                                                    disabled={!selected.outcome?.scheme_service_type_id}
                                                    value={selected.outcome?.scheme_service_subtype_id || ''}
                                                    onChange={(e) => updateOutcome({ scheme_service_subtype_id: e.target.value ? Number(e.target.value) : null })}
                                                >
                                                    <option value="">—</option>
                                                    {(catalog.service_subtypes || []).filter((s) => s.service_type_id === selected.outcome?.scheme_service_type_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </label>
                                            <label className="block text-xs">Cause of call
                                                <select
                                                    className="w-full border rounded px-2 py-1"
                                                    value={selected.outcome?.scheme_cause_of_call_id || ''}
                                                    onChange={(e) => updateOutcome({ scheme_cause_of_call_id: e.target.value ? Number(e.target.value) : null })}
                                                >
                                                    <option value="">—</option>
                                                    {(catalog.causes_of_call || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </label>
                                            <label className="block text-xs">Advice<textarea className="w-full border rounded px-2 py-1" value={selected.outcome?.advice_text || ''} onChange={(e) => updateOutcome({ advice_text: e.target.value })} /></label>
                                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!selected.outcome?.chargeable} onChange={(e) => updateOutcome({ chargeable: e.target.checked })} /> Chargeable</label>
                                        </div>
                                    )}
                                </div>
                            )}
                            {tab === 'Answers' && (
                                <div className="space-y-2">
                                    {(selected.options || []).map((o, i) => (
                                        <div key={i} className="border rounded p-2 text-xs space-y-1">
                                            <input className="w-full border rounded px-1" value={o.label || ''} onChange={(e) => {
                                                const options = [...(selected.options || [])];
                                                options[i] = { ...o, label: e.target.value };
                                                updateNode({ options });
                                            }} />
                                            <input className="w-full border rounded px-1" value={o.value || ''} onChange={(e) => {
                                                const options = [...(selected.options || [])];
                                                options[i] = { ...o, value: e.target.value };
                                                updateNode({ options });
                                            }} />
                                            <select className="w-full border rounded px-1" value={o.target_client_key || o.target_node_id || ''} onChange={(e) => {
                                                const options = [...(selected.options || [])];
                                                options[i] = { ...o, target_client_key: e.target.value, target_node_id: e.target.value };
                                                updateNode({ options });
                                            }}>
                                                <option value="">Target</option>
                                                {nodes.filter((n) => n.client_key !== selected.client_key).map((n) => (
                                                    <option key={n.client_key} value={n.client_key}>{n.code}</option>
                                                ))}
                                            </select>
                                            <button type="button" className="text-red-600" onClick={() => updateNode({ options: (selected.options || []).filter((_, x) => x !== i) })}>Remove</button>
                                        </div>
                                    ))}
                                    <button type="button" className="text-xs border rounded px-2 py-1" onClick={() => updateNode({ options: [...(selected.options || []), { label: 'Option', value: `opt_${(selected.options || []).length + 1}` }] })}>Add option</button>
                                </div>
                            )}
                            {tab === 'Logic' && (
                                <div className="space-y-2 text-xs">
                                    <p className="text-gray-600">Conditions run top-down. COMPUTE nodes always route by condition; QUESTION nodes fall back to these when no answer matches.</p>
                                    {(selected.transitions || []).map((t, i) => {
                                        const cj = t.condition_json || {};
                                        const varDef = (catalog.variables || []).find((v) => v.path === cj.var);
                                        return (
                                            <div key={i} className="border rounded p-2 space-y-1">
                                                <select className="w-full border rounded px-1" value={cj.var || ''} onChange={(e) => updateTransition(i, { condition_json: { ...cj, var: e.target.value, value: '' } })}>
                                                    <option value="">Variable</option>
                                                    {(catalog.variables || []).map((v) => <option key={v.path} value={v.path}>{v.label}</option>)}
                                                </select>
                                                <select className="w-full border rounded px-1" value={cj.op || 'eq'} onChange={(e) => updateTransition(i, { condition_json: { ...cj, op: e.target.value } })}>
                                                    {((catalog.ops && catalog.ops.length) ? catalog.ops : [{ op: 'eq', label: 'equals' }, { op: 'neq', label: 'not equals' }, { op: 'in', label: 'is one of' }, { op: 'exists', label: 'is present' }]).map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                                                </select>
                                                {cj.op !== 'exists' && (
                                                    varDef?.values
                                                        ? (
                                                            <select
                                                                className="w-full border rounded px-1"
                                                                value={Array.isArray(cj.value) ? (cj.value[0] || '') : (cj.value || '')}
                                                                onChange={(e) => updateTransition(i, { condition_json: { ...cj, value: cj.op === 'in' ? [e.target.value] : e.target.value } })}
                                                            >
                                                                <option value="">Value</option>
                                                                {varDef.values.map((v) => <option key={v}>{v}</option>)}
                                                            </select>
                                                        )
                                                        : (
                                                            <input
                                                                className="w-full border rounded px-1"
                                                                placeholder={cj.op === 'in' ? 'comma,separated' : 'value'}
                                                                value={Array.isArray(cj.value) ? cj.value.join(',') : (cj.value ?? '')}
                                                                onChange={(e) => updateTransition(i, { condition_json: { ...cj, value: cj.op === 'in' ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : e.target.value } })}
                                                            />
                                                        )
                                                )}
                                                <select className="w-full border rounded px-1" value={t.target_client_key || t.target_node_id || ''} onChange={(e) => updateTransition(i, { target_client_key: e.target.value, target_node_id: e.target.value })}>
                                                    <option value="">Target</option>
                                                    {nodes.filter((n) => n.client_key !== selected.client_key).map((n) => (
                                                        <option key={n.client_key} value={n.client_key}>{n.code}</option>
                                                    ))}
                                                </select>
                                                <button type="button" className="text-red-600" onClick={() => removeTransition(i)}>Remove</button>
                                            </div>
                                        );
                                    })}
                                    <button type="button" className="border rounded px-2 py-1" onClick={() => updateNode({ transitions: [...(selected.transitions || []), { condition_json: { var: '', op: 'eq', value: '' }, priority: (selected.transitions || []).length, target_client_key: '' }] })}>Add condition</button>
                                </div>
                            )}
                            {tab === 'Settings' && (
                                <div className="space-y-2 text-xs">
                                    <label className="block">Visibility
                                        <select className="w-full border rounded px-2 py-1" value={selected.channel_visibility || 'BOTH'} onChange={(e) => updateNode({ channel_visibility: e.target.value })}>
                                            <option>BOTH</option>
                                            <option>AGENT_ONLY</option>
                                            <option>SELF_SERVICE_ONLY</option>
                                        </select>
                                    </label>
                                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!selected.is_safety_gate} onChange={(e) => updateNode({ is_safety_gate: e.target.checked })} /> Safety gate</label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={!!selected.is_start}
                                            onChange={(e) => {
                                                const on = e.target.checked;
                                                commit((prev) => prev.map((n) => ({
                                                    ...n,
                                                    is_start: n.client_key === selected.client_key ? on : (on ? false : n.is_start),
                                                })));
                                            }}
                                        /> Start node
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                    {validation && (
                        <div className="mt-3 text-xs">
                            <p className={validation.ok ? 'text-green-700' : 'text-red-700'}>{validation.ok ? 'Ready to publish' : 'Blocked'}</p>
                            {(validation.errors || []).map((e, i) => (
                                <button key={`e${i}`} type="button" className="block w-full text-left text-red-600 hover:underline" onClick={() => focusNodeByMsg(e)}>{e}</button>
                            ))}
                            {(validation.warnings || []).map((e, i) => (
                                <button key={`w${i}`} type="button" className="block w-full text-left text-amber-700 hover:underline" onClick={() => focusNodeByMsg(e)}>{e}</button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {simOpen && (
                <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white border-l shadow-xl z-30 flex flex-col">
                    <div className="flex items-center justify-between p-3 border-b">
                        <span className="font-semibold text-sm">Simulator</span>
                        <button type="button" onClick={() => setSimOpen(false)}><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 overflow-auto p-3 space-y-3 text-sm">
                        {!sim && (
                            <button type="button" className="w-full border rounded py-1.5" disabled={simBusy || !flow || !versionId} onClick={simStart}>
                                Start run{flow ? ` · ${flow.entry_category}` : ''}
                            </button>
                        )}
                        {simErr && <p className="text-red-600 text-xs">{simErr}</p>}
                        {sim && (
                            <>
                                <div className="flex gap-2 items-center">
                                    <button type="button" className="text-xs border rounded px-2 py-1" onClick={simBack} disabled={simBusy}><ChevronLeft className="w-3 h-3 inline" />Back</button>
                                    <button type="button" className="text-xs border rounded px-2 py-1" onClick={simStart} disabled={simBusy}><RotateCcw className="w-3 h-3 inline" />Restart</button>
                                    <span className="text-[11px] text-gray-500">{sim.status}</span>
                                </div>
                                {sim.node && (
                                    <div className="border rounded p-2">
                                        <div className="text-[10px] uppercase text-gray-500">{sim.node.type}{sim.node.isSafetyGate ? ' · safety' : ''} · {sim.node.code}</div>
                                        <p className="text-sm mt-1">{sim.node.prompt || <span className="text-gray-400">(no prompt)</span>}</p>
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {(sim.node.options || []).map((o) => (
                                                <button key={o.value} type="button" className="text-xs border rounded px-2 py-1 hover:bg-gray-100" disabled={simBusy} onClick={() => simAnswer(o.value)}>{o.label}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {sim.outcome && (
                                    <div className="border rounded p-2 bg-emerald-50">
                                        <div className="text-[10px] uppercase text-emerald-700">Outcome · {sim.outcome.type}</div>
                                        {sim.outcome.advice && <p className="text-xs mt-1">{sim.outcome.advice}</p>}
                                        {sim.outcome.serviceTypeName && <p className="text-xs">Service: {sim.outcome.serviceTypeName}{sim.outcome.subtypeName ? ` / ${sim.outcome.subtypeName}` : ''}</p>}
                                        {sim.outcome.causeName && <p className="text-xs">Cause: {sim.outcome.causeName}</p>}
                                        {sim.outcome.chargeable && <p className="text-xs text-amber-700">Chargeable</p>}
                                    </div>
                                )}
                                <div>
                                    <p className="text-[11px] uppercase text-gray-500 mb-1">Path</p>
                                    <ol className="space-y-0.5">
                                        {(sim.path || []).map((p, i) => (
                                            <li key={i} className="text-xs text-gray-600">{i + 1}. {p.code} → <span className="font-medium">{p.value}</span>{p.autoAnswered ? ' (auto)' : ''}</li>
                                        ))}
                                    </ol>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Diagnostics;
